import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { MouseEvent, ReactNode } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type {
  TooltipBehavior,
  TooltipContentLayout,
  TooltipLevel,
  TooltipOffset,
  TooltipSize,
  TooltipSpacing,
  TooltipTone,
} from './types';

export interface TooltipProps {
  triggerAsChild?: boolean;
  content: ReactNode;
  children: ReactNode;
  side?: RadixTooltip.TooltipContentProps['side'];
  delayDuration?: number;
  enabled?: boolean;
  level?: TooltipLevel;
  size?: TooltipSize;
  tone?: TooltipTone;
  behavior?: TooltipBehavior;
  spacing?: TooltipSpacing;
  contentLayout?: TooltipContentLayout;
  offset?: TooltipOffset;
  sideOffset?: number;
  align?: RadixTooltip.TooltipContentProps['align'];
  alignOffset?: number;
  open?: RadixTooltip.TooltipProps['open'];
  onOpenChange?: RadixTooltip.TooltipProps['onOpenChange'];
  // Whether the Tooltip should close when the Trigger is clicked.
  closeOnClick?: boolean;
}

const tooltip = variants({
  base: cn('z-50 animate-fade border shadow border-emphasis-subtle'),
  variants: {
    level: {
      1: cn('bg-level-01'),
      2: cn('bg-level-02'),
      3: cn('bg-level-03'),
      4: cn('bg-level-04'),
      5: cn('bg-level-05'),
    },
    size: {
      sm: cn('rounded-xs px-sm py-xs text-body-xs'),
      md: cn('rounded-sm p-md text-body-sm'),
      'deprecated-help': cn('rounded-sm px-md py-sm text-body-sm'),
      scrollList: cn(
        'max-h-[13rem] overflow-y-auto rounded-sm p-md text-body-sm',
      ),
    },
    tone: {
      default: cn('text-default'),
      subtle: cn('text-strong'),
    },
    behavior: {
      interactive: '',
      passive: cn('pointer-events-none'),
    },
    spacing: {
      none: '',
      default: cn('max-w-xs'),
      thin: cn('max-w-[300px]'),
      wide: cn('max-w-2xl'),
      fit: cn('w-fit'),
    },
    contentLayout: {
      default: cn('break-words'),
      centered: cn('text-center'),
      compact: cn('text-body-xs'),
      tight: cn('leading-none'),
      breakAll: cn('break-all'),
    },
    offset: {
      none: '',
      inlineEndXs: cn('me-xs'),
      inlineEndNegSm: cn('-me-sm'),
      blockEndSm: cn('mb-sm'),
    },
  },
  compoundVariants: [
    {
      size: 'deprecated-help',
      contentLayout: 'tight',
      class: cn('leading-4'),
    },
    {
      size: 'deprecated-help',
      contentLayout: 'compact',
      class: cn('text-body-xs'),
    },
  ],
  defaultVariants: {
    level: 1,
    size: 'md',
    tone: 'default',
    behavior: 'interactive',
    spacing: 'none',
    contentLayout: 'default',
    offset: 'none',
  },
});

export function Tooltip({
  content,
  children,
  side,
  level = 1,
  size = 'md',
  tone = 'default',
  behavior = 'interactive',
  spacing = 'none',
  contentLayout = 'default',
  offset = 'none',
  delayDuration,
  enabled = true,
  triggerAsChild = false,
  sideOffset,
  align,
  alignOffset,
  open,
  closeOnClick = true,
  onOpenChange,
}: TooltipProps) {
  const styles = tooltip({
    level,
    size,
    tone,
    behavior,
    spacing,
    contentLayout,
    offset,
  });

  const handleClick = (
    event: MouseEvent | CustomEvent<{ originalEvent: PointerEvent }>,
  ) => {
    if (!closeOnClick) {
      event.preventDefault();
    }
  };

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        delayDuration={delayDuration}
        open={open}
        onOpenChange={onOpenChange}
      >
        {enabled ? (
          <RadixTooltip.Trigger
            asChild={triggerAsChild}
            type="button"
            onClick={handleClick}
            onPointerDown={handleClick}
          >
            {children}
          </RadixTooltip.Trigger>
        ) : (
          <>{children}</>
        )}
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={cn(tooltip.base, styles)}
            side={side}
            sideOffset={sideOffset}
            align={align}
            alignOffset={alignOffset}
            onPointerDownOutside={handleClick}
          >
            {content}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

Tooltip.displayName = 'Tooltip';
